<template>
  <v-dialog :modelValue="dialog" @click:outside="cerrar" @keydown.esc="cerrar" max-width="1300px" transition="dialog-bottom-transition">
    <!--
      El modal abre de una con la tabla en loading: el codigo llega por prop y las lineas despues.
      Al recargar despues de una accion se sigue viendo lo anterior con la tabla en loading.
    -->
    <v-card>
      <v-form>

        <v-card-title>
          <span class="headline">Ubicación {{ grupo ? grupo.code : code }}</span>
        </v-card-title>

        <v-card-text>
          <div class="d-flex align-center flex-wrap ga-3 mb-4" v-if="grupo">
            <span class="text-body-2 text-grey-600">{{ grupo.zona }}</span>
            <div class="status-badge font-weight-medium" :class="service.getLocationStatusClass(grupo.ubicacion.status ? grupo.ubicacion.status.id : null)">
              {{ grupo.ubicacion.status ? grupo.ubicacion.status.name : "-" }}
            </div>
            <v-chip v-if="recontada" color="deep-purple" size="small" variant="tonal">2° conteo</v-chip>
            <v-chip v-if="grupo.pendientes > 0" color="orange" size="small">{{ grupo.pendientes }} sin resolver</v-chip>
            <v-chip v-if="grupo.marcadas > 0" color="green" size="small">{{ grupo.marcadas }} marcadas</v-chip>
          </div>

          <!--
            Mientras espera el segundo conteo las lineas estan en PENDIENTE: no hay nada que decidir.
            El primer conteo sigue a la vista en su columna.
          -->
          <v-alert v-if="esperandoReconteo" type="info" variant="tonal" density="compact" class="mb-3">
            Se pidió un <b>segundo conteo</b> de esta ubicación y el operario todavía no lo hizo. Cuando lo cierre, las líneas vuelven a revisión con los dos conteos a la vista.
          </v-alert>

          <!-- Acciones masivas sobre las líneas tildadas, como en ShipmentOrders.vue -->
          <div class="mb-3" v-if="update_access && lineasSeleccionables.length > 0">
            <v-menu offset-y>
              <template v-slot:activator="{ props }">
                <v-icon style="border: 1px solid; border-radius: 100px; padding: 3px" color="blue-grey" :class="selected.length > 0 ? 'button_blinking' : ''" v-bind="props">
                  mdi-dots-vertical
                </v-icon>
              </template>

              <v-list>
                <v-list-item @click="aprobarSeleccion">
                  <v-list-item-title>Marcar para ajustar</v-list-item-title>
                </v-list-item>
                <v-list-item @click="descartarSeleccion">
                  <v-list-item-title>Descartar las diferencias</v-list-item-title>
                </v-list-item>
              </v-list>
            </v-menu>
            <span v-if="selected.length > 0" class="text-caption text-grey-600 ml-2">{{ selected.length }} seleccionadas</span>
          </div>

          <v-data-table
            :headers="headers"
            :items="grupo ? grupo.lineas : []"
            :loading="loading"
            loading-text="Cargando..."
            no-data-text="Esta ubicación no tiene líneas para mostrar."
            item-value="id"
            :items-per-page="-1"
            :hide-default-footer="true"
            class="conteo-lineas tabla-recuadro"
          >
            <template v-slot:[`header.select`]>
              <v-checkbox
                style="margin: 0px; padding: 0px"
                hide-details
                density="compact"
                :disabled="lineasSeleccionables.length === 0"
                :model-value="todasSeleccionadas"
                :indeterminate="selected.length > 0 && !todasSeleccionadas"
                @update:modelValue="toggleSelectAll"
              />
            </template>
            <template v-slot:item="{ item: linea }">
              <tr>
                <!-- Solo se tildan las que esperan una decisión: el resto ya está resuelta -->
                <td class="col-check" v-if="update_access">
                  <v-checkbox
                    :disabled="!esSeleccionable(linea)"
                    style="margin: 0px; padding: 0px"
                    hide-details
                    density="compact"
                    :model-value="selected.includes(linea.id)"
                    @update:modelValue="toggleSelect(linea)"
                  />
                </td>
                <!-- Mismo diseño que Inventario por LPN: nombre arriba, SKU abajo -->
                <td>
                  <div>
                    {{ linea.producto_name }}
                    <!-- Que es nuevo es lo que el revisor tiene que saber: no suma a una tupla, la da de alta -->
                    <v-tooltip bottom v-if="linea.es_declarado">
                      <template v-slot:activator="{ props }">
                        <v-icon v-bind="props" size="x-small" color="purple">mdi-information-outline</v-icon>
                      </template>
                      <span>Declarado por el operario: esta combinación de producto y atributos todavía no existe en el sistema</span>
                    </v-tooltip>
                  </div>
                  <span class="text-caption text-grey-600 d-block nowrap">SKU: {{ linea.producto_sku }}</span>
                </td>
                <!-- Mismo diseño que Inventario por LPN: el codigo en negrita -->
                <td>
                  <div v-if="linea.lpn">
                    <div class="font-weight-medium">
                      <b>{{ linea.lpn.code }}</b>
                    </div>
                  </div>
                  <span v-else class="text-grey-600">-</span>
                </td>
                <td>
                  <div v-if="linea.atributos.length > 0">
                    <div v-for="attr in linea.atributos" :key="attr.label" class="atributo-row">
                      <span class="font-weight-bold text-caption">{{ attr.label }}:</span>
                      <span class="text-caption text-grey-600 ml-1">{{ attr.value }}</span>
                    </div>
                  </div>
                  <span v-else class="text-caption text-grey-600">-</span>
                </td>
                <!--
                  Los dos numeros van juntos: la diferencia solo se entiende comparandolos. En una
                  ubicacion recontada esta columna es el primer conteo (del historico) y la de al lado
                  el segundo, que es lo vigente en la linea. "-" = la linea no existia en el primero.
                -->
                <td>
                  <div v-if="recontada" class="teorico-contado">{{ linea.theoretical_amount }} / {{ primerConteo(linea) != null ? primerConteo(linea).counted_amount : "-" }}</div>
                  <div v-else class="teorico-contado">{{ linea.theoretical_amount }} / {{ linea.counted_amount != null ? linea.counted_amount : "-" }}</div>
                </td>
                <td v-if="recontada">
                  <div class="teorico-contado">{{ linea.theoretical_amount }} / {{ linea.counted_amount != null ? linea.counted_amount : "-" }}</div>
                </td>
                <!-- Las lineas sin contar todavia no tienen diferencia: no es un cero. En una recontada no va: se lee en las dos columnas de conteo -->
                <td v-if="!recontada" class="text-right">
                  <span v-if="linea.difference == null" class="text-grey-600">-</span>
                  <span v-else class="font-weight-bold" :class="linea.difference < 0 ? 'text-red' : linea.difference > 0 ? 'text-green' : ''">
                    {{ linea.difference > 0 ? "+" : "" }}{{ linea.difference }}
                  </span>
                </td>
                <td>
                  <div class="status-badge font-weight-medium" :class="service.getLineStatusClass(linea.status ? linea.status.id : null)">
                    {{ linea.status ? linea.status.name : "-" }}
                  </div>
                </td>
                <!--
                  Marcada y Hallazgo NO son estados sino etiquetas sobre la linea, por eso van aca y
                  no en la columna de al lado. El icono NO repite el texto del hallazgo —eso ya lo
                  dice el chip— sino lo que el revisor escribio despues.
                -->
                <td>
                  <div class="d-flex align-center flex-wrap ga-1">
                    <v-chip v-if="linea.is_hallazgo" color="purple" size="x-small">Hallazgo</v-chip>
                    <!-- Que dicen los dos conteos juntos (R9). El tooltip dice que hacer con eso. -->
                    <v-tooltip bottom v-if="linea.resultado_reconteo">
                      <template v-slot:activator="{ props }">
                        <v-chip v-bind="props" :color="service.getRecountResultColor(linea.resultado_reconteo.id)" size="x-small">{{ linea.resultado_reconteo.name }}</v-chip>
                      </template>
                      <span>{{ service.getRecountResultHint(linea.resultado_reconteo.id) }}</span>
                    </v-tooltip>
                    <!-- Solo mientras espera el ajuste: sobre una AJU el chip contradice al badge de estado -->
                    <v-chip v-if="linea.approved && service.isPendienteDeResolucion(linea.status ? linea.status.id : null)" color="green" size="x-small">Marcada</v-chip>

                    <v-tooltip bottom v-if="linea.observacion_visible">
                      <template v-slot:activator="{ props }">
                        <v-icon v-bind="props" size="small" color="blue-grey">mdi-comment-text-outline</v-icon>
                      </template>
                      <span style="white-space: pre-line">{{ linea.observacion_visible }}</span>
                    </v-tooltip>
                  </div>
                </td>
                <td class="text-center col-accion">
                  <template v-if="update_access && service.isPendienteDeResolucion(linea.status ? linea.status.id : null)">
                    <v-tooltip bottom v-if="!linea.approved">
                      <template v-slot:activator="{ props }">
                        <v-icon v-bind="props" color="green" class="mr-2" @click="$emit('aprobar', [linea])" size="small">mdi-check</v-icon>
                      </template>
                      <span>Marcar para ajustar</span>
                    </v-tooltip>

                    <v-tooltip bottom>
                      <template v-slot:activator="{ props }">
                        <v-icon v-bind="props" color="red" @click="$emit('descartar', [linea])" size="small">mdi-close</v-icon>
                      </template>
                      <span>Descartar la diferencia</span>
                    </v-tooltip>
                  </template>

                  <!-- Descartar es la unica decision sin vuelta atras: esta la deshace -->
                  <v-tooltip bottom v-else-if="update_access && puedeRevertirDescarte(linea)">
                    <template v-slot:activator="{ props }">
                      <v-icon v-bind="props" color="blue-grey" @click="$emit('revertir', linea)" size="small">mdi-undo-variant</v-icon>
                    </template>
                    <span>Deshacer el descarte y volver a revisión</span>
                  </v-tooltip>
                </td>
              </tr>
            </template>
          </v-data-table>
        </v-card-text>

        <v-card-actions>
          <v-spacer></v-spacer>
          <v-btn color="blue-grey" text @click="cerrar">Cerrar</v-btn>
          <!-- Mismas guardas que el backend (puede_recontar): con diferencia, sin ajustes, primer conteo -->
          <v-btn v-if="update_access && conteo_abierto && grupo && grupo.puede_recontar" color="deep-purple" text :disabled="loading" @click="$emit('recontar', [grupo.ubicacion])"
            >Pedir segundo conteo</v-btn
          >
          <v-btn v-if="update_access && conteo_abierto && esperandoReconteo" color="red" text :disabled="loading" @click="$emit('cancelar-reconteo', grupo.ubicacion)"
            >Cancelar segundo conteo</v-btn
          >
          <!-- El ajuste es por UBICACION: se aplica desde acá sin tener que cerrar y buscar la fila -->
          <v-btn
            v-if="update_access && puedeAprobarUbicacion"
            color="green darken-1"
            text
            :disabled="loading || grupo.marcadas === 0"
            @click="$emit('aplicar', grupo.ubicacion, lineasMarcadas)"
            >Aplicar ajustes</v-btn
          >
        </v-card-actions>
      </v-form>

    </v-card>
  </v-dialog>
</template>

<script>
import { conteoService } from "@/libs/ws/conteoService";

/**
 * Las líneas de UNA ubicación del conteo, con las acciones de revisión.
 *
 * Las líneas las arma el backend (list-lineas-ubicacion): se piden al abrir el modal y otra vez
 * cada vez que la pantalla llama a cargar() después de una acción. Las acciones (marcar,
 * descartar, deshacer, aplicar) se emiten y las resuelve Detalle.vue. Marcar y descartar emiten
 * siempre un array de lineas: una sola desde la fila, o las tildadas desde el menu masivo.
 */
export default {
  props: {
    dialog: Boolean,
    stock_count_location_id: {
      type: Number,
      default: null,
    },
    //El codigo de la ubicacion para el titulo mientras cargan las lineas
    code: {
      type: String,
      default: "",
    },
    update_access: Boolean,
    //Con el conteo cerrado o cancelado no se deshace ningun descarte
    conteo_abierto: Boolean,
  },
  emits: ["close", "aprobar", "descartar", "revertir", "aplicar", "recontar", "cancelar-reconteo"],
  data: () => ({
    service: conteoService,
    detalle: null,
    loading: false,
    //Ids de las lineas tildadas para las acciones masivas
    selected: [],
  }),
  computed: {
    /** La respuesta del backend con el código y la zona a mano para el template. */
    grupo() {
      if (this.detalle == null) return null;
      const u = this.detalle.ubicacion.ubicacion;
      return {
        ...this.detalle,
        code: u ? u.code : "-",
        zona: u && u.zona ? u.zona.name + (u.zona.almacen ? " - " + u.zona.almacen.code : "") : "-",
      };
    },
    /** La ubicacion tuvo (o esta esperando) segundo conteo. */
    recontada() {
      return this.grupo != null && this.grupo.ubicacion.count_number === 2;
    },
    esperandoReconteo() {
      if (this.grupo == null) return false;
      const status = this.grupo.ubicacion.status;
      return status != null && status.id === this.service.LOCATION_RECONTEO;
    },
    puedeAprobarUbicacion() {
      if (this.grupo == null) return false;
      const status = this.grupo.ubicacion.status;
      return status != null && status.id === this.service.LOCATION_CON_DIFERENCIA;
    },
    //Las que va a ajustar "Aplicar ajustes": marcadas y todavía pendientes, mismo criterio que el backend
    lineasMarcadas() {
      if (this.grupo == null) return [];
      return this.grupo.lineas.filter((l) => l.approved === true && this.service.isPendienteDeResolucion(l.status ? l.status.id : null));
    },
    headers() {
      const headers = [
        { title: "Producto", key: "producto_name", sortable: false, align: "start" },
        { title: "LPN", key: "lpn", sortable: false, align: "start" },
        { title: "Atributos", key: "atributos", sortable: false, align: "start" },
        { title: this.recontada ? "Teórico / Conteo 1" : "Teórico / Contado", key: "theoretical_amount", sortable: false, align: "center" },
        ...(this.recontada ? [{ title: "Teórico / Conteo 2", key: "segundo_conteo", sortable: false, align: "center" }] : []),
        ...(this.recontada ? [] : [{ title: "Diferencia", key: "difference", sortable: false, align: "end" }]),
        { title: "Estado", key: "status", sortable: false, align: "start" },
        { title: "Observación", key: "observaciones", sortable: false, align: "start" },
        { title: "Acción", key: "actions", sortable: false, align: "center", minWidth: "110px" },
      ];
      if (this.update_access) headers.unshift({ title: "", key: "select", sortable: false, align: "start", width: "48px" });
      return headers;
    },
    lineasSeleccionables() {
      if (this.grupo == null) return [];
      return this.grupo.lineas.filter((l) => this.esSeleccionable(l));
    },
    todasSeleccionadas() {
      return this.lineasSeleccionables.length > 0 && this.lineasSeleccionables.every((l) => this.selected.includes(l.id));
    },
    lineasSeleccionadas() {
      return this.lineasSeleccionables.filter((l) => this.selected.includes(l.id));
    },
  },
  watch: {
    dialog(val) {
      if (val) {
        //Si lo cerro Detalle (al aplicar ajustes) no paso por cerrar(): la seleccion vieja no sirve
        this.selected = [];
        this.detalle = null;
        this.cargar();
      }
    },
  },
  methods: {
    cargar() {
      if (this.stock_count_location_id == null) return;

      this.loading = true;
      this.service
        .list_lineas_ubicacion({ stock_count_location_id: this.stock_count_location_id })
        .then((response) => {
          if (response.status) {
            this.detalle = response.result;
            //Despues de una accion las lineas cambian de estado: la seleccion arranca de cero
            this.selected = [];
          } else {
            this.toast.error(response.msg);
          }
        })
        .finally(() => {
          this.loading = false;
        });
    },
    /** La pasada 1 del historico. Null en un hallazgo que aparecio recien en el segundo conteo. */
    primerConteo(linea) {
      return (linea.historico || []).find((h) => h.count_number === 1) || null;
    },
    cerrar() {
      this.selected = [];
      this.$emit("close");
    },
    /** Misma condicion que las acciones por linea: solo lo que espera una decision del revisor. */
    esSeleccionable(linea) {
      return this.update_access && this.service.isPendienteDeResolucion(linea.status ? linea.status.id : null);
    },
    toggleSelect(linea) {
      if (this.selected.includes(linea.id)) this.selected = this.selected.filter((id) => id !== linea.id);
      else this.selected = [...this.selected, linea.id];
    },
    toggleSelectAll(isSelected) {
      this.selected = isSelected ? this.lineasSeleccionables.map((l) => l.id) : [];
    },
    aprobarSeleccion() {
      if (this.lineasSeleccionadas.length === 0) {
        this.toast.error("No se ha seleccionado ninguna línea");
        return;
      }
      this.$emit("aprobar", this.lineasSeleccionadas);
    },
    descartarSeleccion() {
      if (this.lineasSeleccionadas.length === 0) {
        this.toast.error("No se ha seleccionado ninguna línea");
        return;
      }
      this.$emit("descartar", this.lineasSeleccionadas);
    },
    /** Solo se deshace mientras la ubicacion siga con diferencias: despues ya se desbloqueó. */
    puedeRevertirDescarte(linea) {
      if (!this.service.isDescartada(linea.status ? linea.status.id : null)) return false;
      if (!this.conteo_abierto) return false;
      return this.puedeAprobarUbicacion;
    },
  },
};
</script>

<style scoped>
/* Los mismos estilos que ModalAplicarAjustes.vue: son scoped y no se comparten */
.tabla-recuadro {
  box-shadow: none !important;
  border: 1px solid rgba(0, 0, 0, 0.12);
  border-radius: 4px;
}
.conteo-lineas :deep(td) {
  padding-top: 8px;
  padding-bottom: 8px;
  vertical-align: middle;
}
.conteo-lineas :deep(.col-check) {
  width: 48px;
  padding-right: 0;
}
.atributo-row {
  line-height: 1.3;
}
/*
  Marcar y descartar van SIEMPRE en el mismo renglon. Sin ancho minimo la columna es la que cede
  cuando Atributos trae varios renglones, y el segundo icono cae abajo del primero.
*/
.conteo-lineas :deep(.col-accion) {
  min-width: 110px;
  white-space: nowrap;
}
/*
  Ancho fijo para que los pares queden en la misma columna visual de una fila a la otra: con el
  ancho por contenido, "1 / 1" y "1200 / 980" no arrancan nunca en el mismo lugar.
*/
.teorico-contado {
  width: 96px;
  margin: 0 auto;
  padding: 4px 8px;
  text-align: center;
  font-weight: bold;
  background: #f5f5f5;
  border-radius: 4px;
}
:deep(.v-chip) {
  font-weight: bold;
}
.nowrap {
  white-space: nowrap;
}
</style>
